<template>
    <div class="udf-section-wrapper" :id="'udf-section-' + section.id">
        <button class="section-header"
                type="button"
                @click="!section.isEditing && !isSearching && $emit('toggle-section', section)"
                :class="{
                    'header-open': section.isOpen,
                    'clickable': !section.isEditing && !isSearching,
                    'header-success': section.toast?.type === 'success',
                    'header-error': section.toast?.type === 'error'
                }">
            <div class="header-left-content">
                <h2 class="header-title">{{ section.title }}</h2>
                <transition name="toast-inline">
                    <div v-if="section.toast" class="toast-message-inline" :class="'toast-' + section.toast.type">
                        <Icon v-if="section.toast.type === 'success'" name="check-circle" color="var(--success-500)" size="lg"/>
                        <Icon v-if="section.toast.type === 'error'" name="exclamation-circle" color="var(--danger-500)" size="lg"/>
                        <span>{{ section.toast.message }}</span>
                    </div>
                </transition>
            </div>
            <div class="header-actions">
                <button v-show="section.isEditing" @click.stop="$emit('cancel-section-edit', section)" @keydown.enter.prevent="$emit('cancel-section-edit', section)"
                        class="button-cancel">
                    Cancel
                </button>
                <button v-show="section.isEditing" @click.stop="$emit('save-section-edit', section)" class="button-save"
                        ref="saveButtonRef">
                    Save
                </button>
                <button v-show="!section.isEditing && !isSearching" @click.stop="$emit('enable-section-edit', section)" @keydown.enter.prevent="$emit('enable-section-edit', section)"
                        class="button-edit-section" title="Edit Group">
                    <Icon name="pencil" size="lg" color="var(--primary-400)" hover="var(--primary-700)"></Icon>
                </button>
                <button v-if="!section.isEditing && !isSearching"
                        @click.stop.prevent="$emit('toggle-section', section)"
                        class="chevron-button"
                        :title="section.isOpen ? 'Close Section' : 'Open Section'">
                    <Icon name="chevron-down"
                          size="xl" color="var(--primary-400)"
                          hover="var(--primary-700)" class="chevron-icon"
                          :class="{ 'rotated': section.isOpen }"/>
                </button>
            </div>
        </button>

        <div v-show="section.isOpen || isSearching" class="section-body" :class="{ 'section-is-editing': section.isEditing }" @keydown.esc.prevent="$emit('cancel-section-edit', section)">
            <div class="grid-container">
                <div v-for="(field, fieldIndex) in section.fields" :key="field.id"
                     :class="['field-container', field.colSpan === 2 ? 'col-span-2' : 'col-span-1']"
                     :id="'field-container-' + field.id"
                     @keydown.tab="section.isEditing && fieldIndex === section.fields.length - 1 ? handleLastFieldTab($event) : null">

                    <udf-field
                        :field="field"
                        :is-section-editing="section.isEditing"
                        @enable-field-edit="$emit('enable-field-edit', $event)"
                        @cancel-field-edit="$emit('cancel-field-edit', $event)"
                        @save-field-edit="$emit('save-field-edit', $event)"
                        @handle-field-tab="passFieldTab"
                        @handle-enter-key="passEnterKey"
                        :ref="el => { if(el) fieldRefs[field.id] = el }"
                    />

                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@common/icon/Icon.vue';
import udfField from './udfField.vue';

const props = defineProps({
    section: {
        type: Object,
        required: true
    },
    isSearching: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits([
    'cancel-section-edit',
    'save-section-edit',
    'enable-section-edit',
    'enable-field-edit',
    'cancel-field-edit',
    'save-field-edit',
    'handle-field-tab',
    'handle-enter-key',
    'toggle-section'
]);

const saveButtonRef = ref(null);
const fieldRefs = ref({});

function handleLastFieldTab(event) {
    if (event.shiftKey) return;
    const container = event.currentTarget;
    const focusable = Array.from(container.querySelectorAll('input:not([type=hidden]), textarea, select, [tabindex]:not([tabindex="-1"])'));
    const lastFocusableElement = focusable[focusable.length - 1];

    if (event.target === lastFocusableElement) {
        event.preventDefault();
        saveButtonRef.value?.focus();
    }
}

function passFieldTab(event, field) {
    emit('handle-field-tab', props.section, field, event);
}

function passEnterKey(event, field) {
    emit('handle-enter-key', event, field, props.section);
}

defineExpose({
    focusField(fieldId) {
        if (fieldRefs.value[fieldId]) {
            fieldRefs.value[fieldId].focus();
        }
    },
    focusSaveButton() {
        if (saveButtonRef.value) {
            saveButtonRef.value.focus();
        }
    }
});
</script>

<style scoped>
.udf-section-wrapper {
    background: var(--surface-default);
}

/* Hide the bottom border of a section header if the next section is open */
.udf-section-wrapper:has(+ .udf-section-wrapper .section-header.header-open) .section-header {
    border-bottom-color: transparent;
}

.section-header {
    background: var(--surface-subtle);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) var(--space-8);
    transition: background-color 0.2s, border-left 0.2s;
    border: none;
    border-left: 3px solid transparent;
    outline: none !important;
    width: 100%;
    font-family: inherit;
    text-align: left;
}

.section-header:focus {
    outline: none !important;
}

.section-header:focus-visible {
    outline: none !important;
    box-shadow: inset 0 0 0 2px var(--primary-500);
}

.section-header:not(.header-open) {
    border-bottom: 1px solid var(--grayscale-100);
}

/* The last closed header should not have a bottom border */
.udf-section-wrapper:last-child .section-header:not(.header-open) {
    border-bottom: none;
}

.section-header.header-open {
    background: var(--grayscale-100);
    border-bottom: 1px solid transparent;
    border-left: 3px solid var(--primary-500);
}

.section-header.header-success {
    background-color: var(--success-50) !important;
    border-left: 3px solid var(--success-700) !important;
}

.section-header.header-success .header-title {
    color: var(--success-800) !important;
}

.section-header.header-error {
    background-color: var(--danger-50) !important;
    border-left: 3px solid var(--danger-700) !important;
}

.section-header.header-error .header-title {
    color: var(--danger-800) !important;
}

/* The last section's grid container needs a bottom border when open to close the side borders */
.udf-section-wrapper:last-child .grid-container {
    border-bottom: 1px solid #eee;
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

.section-header.header-open h2 {
    color: var(--primary-700);
}

.section-header.clickable:hover {
    background: var(--grayscale-100);
    cursor: pointer;
}

.header-left-content {
    display: flex;
    align-items: center;
    gap: var(--space-6);
}

.header-title {
    font-size: var(--text-size-h3);
    font-weight: var(--text-weight-semibold);
    color: var(--grayscale-700);
    margin: 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: var(--space-4);
}

button {
    border: none;
    background: none;
    cursor: pointer;
}

.button-edit-section {
    display: none;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    border: none;
    background: transparent;
}

.section-header:hover .button-edit-section,
.section-header:focus-within .button-edit-section,
.section-header.header-open .button-edit-section {
    display: flex;
}

.button-cancel {
    background: var(--surface-default);
    padding: var(--space-2) var(--space-4);
    color: var(--grayscale-600);
    font-weight: var(--text-weight-medium);
    border: 1px solid var(--border-color-disabled);
    border-radius: var(--radius-xs);
}

.button-cancel:hover {
    background-color: var(--grayscale-100);
}

.button-save {
    padding: var(--space-2) var(--space-4);
    background-color: var(--primary-500);
    color: var(--surface-default);
    font-weight: var(--text-weight-medium);
    border-radius: var(--radius-xs);
}

.button-save:hover {
    background-color: var(--primary-700);
}

.chevron-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-2);
    border-radius: var(--radius-full);
}

.chevron-button:hover {
    background-color: var(--primary-50);
}

.chevron-icon {
    transition: transform 0.3s ease;
}

.chevron-icon.rotated {
    transform: rotate(180deg);
}

.section-is-editing {
    background: var(--primary-50);
}

.grid-container {
    border: 1px solid #eee;
    border-top: none;
    border-bottom: none;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    padding: 1rem;
}

.col-span-2 {
    grid-column: span 2 / span 2;
}

.field-container {
    padding: var(--space-4) var(--space-6);
}

.field-container:hover{
    //background-color: var(--grayscale-100);
}

.toast-message-inline {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-size-sm);
    font-weight: var(--text-weight-medium);
}

.toast-success {
    color: var(--success-700);
}

.toast-error {
    color: var(--danger-700);
}

.toast-inline-enter-active,
.toast-inline-leave-active {
    transition: all 0.2s ease-in-out;
}

.toast-inline-enter-from,
.toast-inline-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}

@media (max-width: 1350px) {
    .grid-container {
        grid-template-columns: 1fr;
    }
    .col-span-2 {
        grid-column: span 1 / span 1;
    }
}

@media (max-width: 1024px) {
    .grid-container {
        grid-template-columns: 1fr;
    }
}
</style>
